import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from 'recharts';
import { MessagesSquare, Scale, Info, Users, Lightbulb } from 'lucide-react';
import { tokenizeAll, hedgingWords, powerWords, agencyWords, passiveIndicators, countWordMatches, generateAPAInterpretation } from './qualitativeUtils';

const DiscourseAnalysis = ({ texts }) => {
  const analysis = useMemo(() => {
    const metrics = texts.map(text => {
      const words = tokenizeAll(text);
      const totalWords = words.length;

      const hedging = countWordMatches(words, hedgingWords);
      const power = countWordMatches(words, powerWords);
      const agency = countWordMatches(words, agencyWords);
      const passive = countWordMatches(words, passiveIndicators);

      const pronouns = {
        firstSingular: words.filter(w => ['i', 'me', 'my', 'myself', 'mine'].includes(w)).length,
        firstPlural: words.filter(w => ['we', 'us', 'our', 'ourselves', 'ours'].includes(w)).length,
        secondPerson: words.filter(w => ['you', 'your', 'yourself', 'yours'].includes(w)).length,
        thirdPerson: words.filter(w => ['he', 'she', 'they', 'him', 'her', 'them', 'his', 'hers', 'their', 'theirs'].includes(w)).length
      };

      return {
        text,
        totalWords,
        hedging,
        power,
        agency,
        passive,
        pronouns,
        hedgingRate: totalWords > 0 ? (hedging / totalWords) * 100 : 0,
        powerRate: totalWords > 0 ? (power / totalWords) * 100 : 0,
        agencyRate: totalWords > 0 ? (agency / totalWords) * 100 : 0,
        passiveRate: totalWords > 0 ? (passive / totalWords) * 100 : 0
      };
    });

    const totals = metrics.reduce((acc, m) => ({
      hedging: acc.hedging + m.hedging,
      power: acc.power + m.power,
      agency: acc.agency + m.agency,
      passive: acc.passive + m.passive,
      totalWords: acc.totalWords + m.totalWords,
      pronouns: {
        firstSingular: acc.pronouns.firstSingular + m.pronouns.firstSingular,
        firstPlural: acc.pronouns.firstPlural + m.pronouns.firstPlural,
        secondPerson: acc.pronouns.secondPerson + m.pronouns.secondPerson,
        thirdPerson: acc.pronouns.thirdPerson + m.pronouns.thirdPerson
      }
    }), { 
      hedging: 0, power: 0, agency: 0, passive: 0, totalWords: 0,
      pronouns: { firstSingular: 0, firstPlural: 0, secondPerson: 0, thirdPerson: 0 }
    });

    const hedgingPercentage = totals.totalWords > 0 ? Math.round((totals.hedging / totals.totalWords) * 1000) / 10 : 0;
    const powerPercentage = totals.totalWords > 0 ? Math.round((totals.power / totals.totalWords) * 1000) / 10 : 0;
    const agencyRatio = totals.passive > 0 ? (totals.agency / totals.passive).toFixed(2) : 'N/A';
    
    let agencyLevel = 'moderate';
    if (agencyRatio !== 'N/A') {
      if (parseFloat(agencyRatio) > 2) agencyLevel = 'high';
      else if (parseFloat(agencyRatio) < 0.5) agencyLevel = 'low';
    }

    const highHedgingExamples = metrics
      .filter(m => m.hedgingRate > 5)
      .sort((a, b) => b.hedgingRate - a.hedgingRate)
      .slice(0, 3);

    const highPowerExamples = metrics
      .filter(m => m.powerRate > 3)
      .sort((a, b) => b.powerRate - a.powerRate)
      .slice(0, 3);

    return {
      totalResponses: texts.length,
      totalWords: totals.totalWords,
      hedgingCount: totals.hedging,
      powerCount: totals.power,
      agencyCount: totals.agency,
      passiveCount: totals.passive,
      hedgingPercentage,
      powerPercentage,
      agencyLevel,
      agencyRatio,
      pronouns: totals.pronouns,
      highHedgingExamples,
      highPowerExamples,
      metrics
    };
  }, [texts]);

  const interpretation = useMemo(() => 
    generateAPAInterpretation('discourse', analysis),
    [analysis]
  );

  const discourseSummary = useMemo(() => {
    const totalPronouns = analysis.pronouns.firstSingular + analysis.pronouns.firstPlural + analysis.pronouns.secondPerson + analysis.pronouns.thirdPerson;
    const dominantPronoun = analysis.pronouns.firstSingular > analysis.pronouns.firstPlural ? 'first-person singular ("I/me")' : 'first-person plural ("we/us")';
    const pronounPercent = totalPronouns > 0 ? Math.round((Math.max(analysis.pronouns.firstSingular, analysis.pronouns.firstPlural) / totalPronouns) * 100) : 0;
    
    return `This discourse analysis explored language patterns and power dynamics across ${analysis.totalResponses} text segments containing ${analysis.totalWords.toLocaleString()} total words. Hedging language (expressions of uncertainty like "maybe," "perhaps," "might") comprised ${analysis.hedgingPercentage}% of content, while power-related terms (authority, control, necessity) appeared in ${analysis.powerPercentage}% of text. The agency analysis reveals a ${analysis.agencyLevel} level of participant agency, with an agency-to-passivity ratio of ${analysis.agencyRatio}. ${analysis.agencyLevel === 'high' ? 'Participants demonstrate strong ownership of their experiences and actions.' : analysis.agencyLevel === 'low' ? 'Participants frequently position themselves as recipients of actions rather than agents.' : 'Participants show balanced attribution between active and passive roles.'} Pronoun analysis shows dominance of ${dominantPronoun} forms (${pronounPercent}% of pronouns), suggesting ${analysis.pronouns.firstSingular > analysis.pronouns.firstPlural ? 'individual-focused narratives' : 'collective or inclusive framing'}. Second-person pronouns ("you") appeared ${analysis.pronouns.secondPerson} times, indicating ${analysis.pronouns.secondPerson > 10 ? 'direct engagement with readers/listeners' : 'minimal direct address'}. These linguistic patterns reveal how participants construct their social reality and position themselves relative to power structures.`;
  }, [analysis]);

  const languagePatternData = [
    { pattern: 'Hedging', count: analysis.hedgingCount, color: '#f59e0b' },
    { pattern: 'Power Terms', count: analysis.powerCount, color: '#ef4444' },
    { pattern: 'Agency', count: analysis.agencyCount, color: '#22c55e' },
    { pattern: 'Passive', count: analysis.passiveCount, color: '#6366f1' }
  ];

  const pronounData = [
    { pronoun: 'I/Me/My', count: analysis.pronouns.firstSingular },
    { pronoun: 'We/Us/Our', count: analysis.pronouns.firstPlural },
    { pronoun: 'You/Your', count: analysis.pronouns.secondPerson },
    { pronoun: 'They/He/She', count: analysis.pronouns.thirdPerson }
  ];

  const radarData = [
    { dimension: 'Hedging', value: Math.min(analysis.hedgingPercentage * 10, 100) },
    { dimension: 'Power', value: Math.min(analysis.powerPercentage * 10, 100) },
    { dimension: 'Agency', value: analysis.totalWords > 0 ? Math.min((analysis.agencyCount / analysis.totalWords) * 1000, 100) : 0 },
    { dimension: 'Passive', value: analysis.totalWords > 0 ? Math.min((analysis.passiveCount / analysis.totalWords) * 1000, 100) : 0 }
  ];

  return (
    <div className="discourse-analysis">
      <div className="analysis-header">
        <h4><MessagesSquare size={20} /> Discourse Analysis</h4>
        <p>Analyze language patterns, power dynamics, and voice in text</p>
      </div>

      <div className="analysis-guidance">
        <Lightbulb size={18} />
        <p>Here are some charts/graphs and some analytical content to get you started. You will need to expand on these and possibly make connections and triangulation with other data analysis.</p>
      </div>

      <div className="stats-row">
        <div className="stat-box">
          <div className="stat-value">{analysis.totalResponses}</div>
          <div className="stat-label">Responses Analyzed</div>
        </div>
        <div className="stat-box">
          <div className="stat-value">{analysis.hedgingPercentage}%</div>
          <div className="stat-label">Hedging Language</div>
        </div>
        <div className="stat-box">
          <div className="stat-value">{analysis.powerPercentage}%</div>
          <div className="stat-label">Power Terms</div>
        </div>
        <div className="stat-box highlight">
          <div className="stat-value">{analysis.agencyLevel}</div>
          <div className="stat-label">Agency Level</div>
        </div>
      </div>

      <div className="chart-section">
        <h5>Language Pattern Profile</h5>
        <ResponsiveContainer width="100%" height={280}>
          <RadarChart data={radarData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 12 }} />
            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
            <Radar
              name="Discourse Profile"
              dataKey="value"
              stroke="#6366f1"
              fill="#6366f1"
              fillOpacity={0.5}
            />
            <Tooltip formatter={(value) => [`${Math.round(value)}`, 'Score']} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <div className="two-column-charts">
        <div className="chart-section">
          <h5><Scale size={16} /> Language Pattern Counts</h5>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={languagePatternData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="pattern" tick={{ fontSize: 11 }} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-section">
          <h5><Users size={16} /> Pronoun Usage</h5>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={pronounData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="pronoun" tick={{ fontSize: 10 }} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="discourse-insights">
        <div className="insight-card hedging">
          <h5>Hedging Language</h5>
          <p className="insight-desc">Words that express uncertainty or tentativeness (maybe, perhaps, might, seem)</p>
          <div className="insight-stat">
            <span className="big-num">{analysis.hedgingCount}</span> instances found
          </div>
          {analysis.highHedgingExamples.length > 0 && (
            <div className="examples">
              <span className="example-label">High hedging example:</span>
              <p>"{analysis.highHedgingExamples[0].text.substring(0, 120)}..."</p>
            </div>
          )}
        </div>

        <div className="insight-card power">
          <h5>Power & Authority</h5>
          <p className="insight-desc">Words expressing control, necessity, or authority (must, should, require, control)</p>
          <div className="insight-stat">
            <span className="big-num">{analysis.powerCount}</span> instances found
          </div>
          {analysis.highPowerExamples.length > 0 && (
            <div className="examples">
              <span className="example-label">High power example:</span>
              <p>"{analysis.highPowerExamples[0].text.substring(0, 120)}..."</p>
            </div>
          )}
        </div>

        <div className="insight-card agency">
          <h5>Agency vs Passivity</h5>
          <p className="insight-desc">Balance between active participant and passive recipient language</p>
          <div className="insight-stat">
            <span className="big-num">{analysis.agencyRatio}</span> agency ratio
          </div>
          <div className="agency-bar">
            <div className="agency-label">Passive</div>
            <div className="bar-container">
              <div 
                className="agency-fill" 
                style={{ 
                  width: `${analysis.agencyCount + analysis.passiveCount > 0 
                    ? (analysis.agencyCount / (analysis.agencyCount + analysis.passiveCount)) * 100 
                    : 50}%` 
                }}
              />
            </div>
            <div className="agency-label">Agentic</div>
          </div>
        </div>
      </div>

      <div className="interpretation-section">
        <h5><Info size={18} /> Analytical Summary</h5>
        <p>{discourseSummary}</p>
      </div>
    </div>
  );
};

export default DiscourseAnalysis;
